namespace e5 {
    export interface ClampBoundsRect {
        top?: number | string;
        right?: number | string;
        bottom?: number | string;
        left?: number | string;
    }

    export type ClampBounds = ClampBoundsRect | number | string | "auto";

    export interface ClampOptions {
        boundsParent?: ClampBounds;
        parent?: HTMLElement | "auto";
        bounds?: ClampBounds;
        transformMode?: ITransformMode;
        useTransform?: boolean;
        tweenTime?: number;
        clampX?: boolean;
        clampY?: boolean;
    }

    export class Clamp implements ClampOptions {
        public static DEFAULT: ClampOptions = {
            bounds: "auto",
            boundsParent: "auto",
            parent: "auto",
            transformMode: TransformMode.Rectangle,
            useTransform: true,
            tweenTime: 0,
            clampX: true,
            clampY: true
        };

        private static _clamp: Clamp = new Clamp();

        public bounds: ClampBounds;
        public boundsParent: ClampBounds;
        public parent: HTMLElement | "auto";
        public useTransform: boolean;
        public tweenTime: number;
        public clampX: boolean;
        public clampY: boolean;

        private _boundsParent = new Rect();
        private _bounds = new Rect();
        private _transform: Transform = new Transform();
        private _transformParent: Transform = new Transform();

        constructor(options: ClampOptions = null) {
            this.setOptions(options);
        }

        public setOptions(options: ClampOptions) {
            options = ObjectUtil.extend({}, Clamp.DEFAULT, options);
            this.bounds = options.bounds;
            this.boundsParent = options.boundsParent;
            this.parent = options.parent;
            this.transformMode = options.transformMode;
            this.useTransform = options.useTransform;
            this.tweenTime = options.tweenTime;
            this.clampX = options.clampX;
            this.clampY = options.clampY;
        }

        public get transformMode(): ITransformMode {
            return this._transform.mode;
        }

        public set transformMode(mode: ITransformMode) {
            this._transform.mode = mode;
            this._transformParent.mode = mode;
        }

        protected apply(element: HTMLElement): void {
            if (!element) {
                console.warn("Missing element parameter.");
                return;
            }

            var parentElement: HTMLElement = this.parent === "auto" ? element.parentElement : this.parent;

            this._transform.update(element);

            var initialX = this._transform.matrix.m41;
            var initialY = this._transform.matrix.m42;

            this._transformParent.update(parentElement);

            if (this.tweenTime > 0) {
                var inLeft = Calc.parse(element.style.left, 0);
                var inTop = Calc.parse(element.style.top, 0);
                var inMatrix = this._transform.matrix.getCss3d();
            }

            this.setRect(parentElement, this.boundsParent, this._boundsParent);
            this.setRect(element, this.bounds, this._bounds);

            /**
             * The limits of the bounds-parent.
             */
            var minX = this._boundsParent.x;
            var minY = this._boundsParent.y;
            var maxX = this._boundsParent.right;
            var maxY = this._boundsParent.bottom;

            var anchors = this._bounds.toPoints();

            //UNDER TESTING...
            var useTransformMatrixOperations = this.useTransform;

            /**
             * Clamp each anchor point (top-left, top-right, bottom-right and bottom-left).
             */
            var isDirty = false;
            for (var anchor of anchors) {

                /**
                 * If the preview clamping does not change the transformation,
                 * then we can skip updating the transformation.
                 */
                if (isDirty)
                    this._transform.update(element);

                var anchorX = anchor.x;
                var anchorY = anchor.y;

                /**
                 * Clamp in "parent" coordinate system.
                 */
                var localParent = this._transform.localToLocal(this._transformParent, anchorX, anchorY, 0);
                var x = localParent.x;
                var y = localParent.y;
                x = Calc.clamp(x, minX, maxX);
                y = Calc.clamp(y, minY, maxY);
                if (localParent.x === x && localParent.y === y)
                    continue;
                var local = this._transformParent.localToLocal(this._transform, x, y, 0);

                /**
                 * Apply the local delta-transformation to get the deltaX and deltaY.
                 */
                local.x -= anchorX;
                local.y -= anchorY;
                this._transform.matrix.deltaTransform(local, local);

                if (useTransformMatrixOperations) {
                    // this._transform.append(Matrix3d.makePosition(local));
                    this._transform.prependPosition(local);
                }
                else
                    this._transform.matrix.prependPosition(local);

                if (this.useTransform) {
                    if (!useTransformMatrixOperations)
                        element.style.transform = this._transform.matrix.getCss3d();
                }
                else {
                    var left = Calc.parse(element.style.left, 0) + this._transform.matrix.m41;
                    var top = Calc.parse(element.style.top, 0) + this._transform.matrix.m42;
                    element.style.left = left + "px";
                    element.style.top = top + "px";
                }

                isDirty = !useTransformMatrixOperations;
            }

            if (!this.clampX) {
                this._transform.matrix.m41 = initialX;
            }
            if (!this.clampY) {
                this._transform.matrix.m42 = initialY;
            }

            if (useTransformMatrixOperations) {
                if (this.tweenTime > 0) {
                    gs.TweenMax.to(element, this.tweenTime, { matrix3d: this._transform.matrix });
                }
                else {
                    element.style.transform = this._transform.matrix.getCss3d();
                }
            }
            else {
                /**
                 * Reset to inital values and tween to clamped values if needed.
                 * If no translation appears, then just skip.
                 */
                if (this.tweenTime > 0) {
                    var matrix = this._transform.matrix;
                    if (this.useTransform) {
                        var targetCss = matrix.getCss3d();
                        if (targetCss === inMatrix)
                            return;
                        element.style.transform = inMatrix;
                        gs.TweenMax.to(element, this.tweenTime, { matrix3d: targetCss });
                    } else {
                        var left = Calc.parse(element.style.left, 0);
                        var top = Calc.parse(element.style.top, 0);
                        if (inLeft === left && inTop === top)
                            return;
                        element.style.left = inLeft + "px";
                        element.style.top = inTop + "px";
                        gs.TweenMax.to(element, this.tweenTime, { left: this.clampX ? left : inLeft, top: this.clampY ? top : inTop });
                    }
                }
            }
        }

        private setRect(element: HTMLElement, bounds: ClampBounds, rect: Rect): Rect {
            if (bounds === "auto" || bounds === undefined) {
                rect.x = 0;
                rect.y = 0;
                rect.width = element.offsetWidth;
                rect.height = element.offsetHeight;
            }
            else if (typeof bounds === "string") {
                var valueX = Calc.relationCalced(bounds, element.offsetWidth, 0);
                var valueY = Calc.relationCalced(bounds, element.offsetHeight, 0);
                rect.x = valueX;
                rect.y = valueY;
                rect.right = element.offsetWidth - valueX;
                rect.bottom = element.offsetHeight - valueY;
            }
            else if (typeof bounds === "number") {
                rect.x = bounds;
                rect.y = bounds;
                rect.right = element.offsetWidth - bounds;
                rect.bottom = element.offsetHeight - bounds;
            }
            else {
                rect.x = Calc.relationCalced(bounds.left, element.offsetWidth, 0);
                rect.y = Calc.relationCalced(bounds.top, element.offsetHeight, 0);
                rect.right = element.offsetWidth - Calc.relationCalced(bounds.right, element.offsetWidth, 0);
                rect.bottom = element.offsetHeight - Calc.relationCalced(bounds.bottom, element.offsetHeight, 0);
            }

            return rect;
        }

        public dispose(): void {
            this._bounds = null;
            this._boundsParent = null;
            this._transform.dispose();
            this._transform = null;
            this._transformParent.dispose();
            this._transformParent = null;
        }

        public static apply(element: HTMLElement, options: ClampOptions = null): void {
            this._clamp.setOptions(options);
            this._clamp.apply(element);
        }
    }
}